<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<script lang="ts">
  import Panel from '@components/panel.svelte'
  import Image from '@components/image.svelte'
  import EntityLink from '@components/entity-link.svelte'
  import dayjs from 'dayjs'
  import { selectedId, selectedPlayer } from './stores'
  import {
    getValue,
    type GameraTeamReference,
    type NflStatKey,
    type StatModel,
  } from '@statmuse/core/gamera'
  import Icon from '@components/icon.svelte'
  import { isNativeMobile } from '@lib/stores'
  import { onMount } from 'svelte'
  import { round } from 'lodash-es'

  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined
  export let gameTimestamp: string
  export let domain: 'NFL' | 'CFB'

  $: opponentTeam = $selectedId?.alignment === 'away' ? homeTeam : awayTeam

  type DisplayFn = (s?: StatModel<NflStatKey>) => string

  $: statSections = (
    $selectedPlayer?.player?.position === 'QUARTERBACK'
      ? [
          { key: 'Comp', statKey: 'Passing-Completions' },
          { key: 'Att', statKey: 'Passing-Attempts' },
          {
            key: 'Pct',
            statKey: 'Passing-Completions',
            display: (s) => {
              return `${round(
                (getValue(s ?? {}, 'Passing-Completions') /
                  getValue(s ?? {}, 'Passing-Attempts')) *
                  100,
                1,
              )}%`
            },
          },
          { key: 'Yds', statKey: 'Passing-Yards' },
          { key: 'Avg', statKey: 'Passing-YardsPerAttempt' },
          { key: 'TD', statKey: 'Passing-Touchdowns' },
          { key: 'Int', statKey: 'Passing-Interceptions' },
          { key: 'Sacked', statKey: 'Passing-Sacks' },
          // { key: 'Long', statKey: 'Passing-Yards' },
          { key: 'Rating', statKey: 'Passing-Rating' },
          {
            key: 'Total Yds',
            statKey: 'Rushing-Yards',
            display: (s) =>
              getValue(s ?? {}, 'Passing-Yards') +
              getValue(s ?? {}, 'Rushing-Yards') +
              getValue(s ?? {}, 'Receiving-Yards'),
          },
          {
            key: 'Total TD',
            statKey: 'Rushing-Touchdowns',
            display: (s) =>
              getValue(s ?? {}, 'Passing-Touchdowns') +
              getValue(s ?? {}, 'Rushing-Touchdowns') +
              getValue(s ?? {}, 'Receiving-Touchdowns'),
          },
          {
            key: 'Fum (L)',
            statKey: 'Fumbles',
            display: (s) =>
              `${getValue(s ?? {}, 'Fumbles')} (${getValue(
                s ?? {},
                'FumblesLost',
              )})`,
          },
        ]
      : $selectedPlayer?.player?.position === 'RUNNING_BACK'
      ? [
          { key: 'Att', statKey: 'Rushing-Attempts' },
          { key: 'Rush Yds', statKey: 'Rushing-Yards' },
          { key: 'Avg', statKey: 'Rushing-YardsPerAttempt' },
          { key: 'TD', statKey: 'Rushing-Touchdowns' },
          { key: 'Long', statKey: 'Rushing-LongestRush' },
          { key: 'Rec', statKey: 'Receiving-Receptions' },
          { key: 'Rec Yds', statKey: 'Receiving-Yards' },
          { key: 'Rec TD', statKey: 'Receiving-Touchdowns' },
          {
            key: 'Total Yds',
            statKey: 'Rushing-Yards',
            display: (s) =>
              getValue(s ?? {}, 'Rushing-Yards') +
              getValue(s ?? {}, 'Receiving-Yards'),
          },
          {
            key: 'Total TD',
            statKey: 'Rushing-Touchdowns',
            display: (s) =>
              getValue(s ?? {}, 'Rushing-Touchdowns') +
              getValue(s ?? {}, 'Receiving-Touchdowns'),
          },
          {
            key: 'Fum (L)',
            statKey: 'Fumbles',
            display: (s) =>
              `${getValue(s ?? {}, 'Fumbles')} (${getValue(
                s ?? {},
                'FumblesLost',
              )})`,
          },
        ]
      : ['WIDE_RECEIVER', 'TIGHT_END'].includes(
          $selectedPlayer?.player?.position ?? '',
        )
      ? [
          { key: 'Rec', statKey: 'Receiving-Receptions' },
          { key: 'Trg', statKey: 'Receiving-Targets' },
          { key: 'Yds', statKey: 'Receiving-Yards' },
          { key: 'Avg', statKey: 'Receiving-YardsPerReception' },
          { key: 'TD', statKey: 'Receiving-Touchdowns' },
          { key: 'Long', statKey: 'Receiving-LongestReception' },
          {
            key: 'Total Yds',
            statKey: 'Rushing-Yards',
            display: (s) =>
              getValue(s ?? {}, 'Rushing-Yards') +
              getValue(s ?? {}, 'Receiving-Yards'),
          },
          {
            key: 'Total TD',
            statKey: 'Rushing-Touchdowns',
            display: (s) =>
              getValue(s ?? {}, 'Rushing-Touchdowns') +
              getValue(s ?? {}, 'Receiving-Touchdowns'),
          },
          {
            key: 'Fum (L)',
            statKey: 'Fumbles',
            display: (s) =>
              `${getValue(s ?? {}, 'Fumbles')} (${getValue(
                s ?? {},
                'FumblesLost',
              )})`,
          },
        ]
      : ['DEFENSIVE_LINEMAN', 'DEFENSIVE_BACK', 'LINEBACKER'].includes(
          $selectedPlayer?.player?.position ?? '',
        )
      ? [
          { key: 'Comb Tkl', statKey: 'Defensive-CombinedTackles' },
          { key: 'Solo Tkl', statKey: 'Defensive-Tackles' },
          {
            key: 'Ast Tkl',
            statKey: 'Defensive-Tackles',
            display: (s) =>
              getValue(s ?? {}, 'Defensive-CombinedTackles') -
              getValue(s ?? {}, 'Defensive-Tackles'),
          },
          { key: 'TFL', statKey: 'Defensive-TacklesForLoss' },
          { key: 'Sacks', statKey: 'Defensive-Sacks' },
          { key: 'FF', statKey: 'Defensive-ForcedFumbles' },
          { key: 'Int', statKey: 'Defensive-Interceptions' },
        ]
      : $selectedPlayer?.player?.position === 'KICKER'
      ? [
          { key: 'FGM', statKey: 'Kicking-FieldGoalsMade' },
          { key: 'FGA', statKey: 'Kicking-FieldGoalsAttempted' },
          { key: 'XPM', statKey: 'Kicking-ExtraPointsMade' },
          { key: 'XPA', statKey: 'Kicking-ExtraPointsAttempted' },
        ]
      : $selectedPlayer?.player?.position === 'PUNTER'
      ? [
          { key: 'Punts', statKey: 'Punting-Punts' },
          { key: 'Yds', statKey: 'Punting-Yards' },
          { key: 'Avg', statKey: 'Punting-YardsAverage' },
          { key: 'Long', statKey: 'Punting-LongestPunt' },
        ]
      : []
  ) as {
    key: string
    statKey: NflStatKey
    display?: DisplayFn
  }[]

  // $: {
  //   if ($selectedPlayer) {
  //     // disable body scroll
  //     document.body.style.top = `-${window.scrollY}px`
  //     document.body.style.position = 'fixed'
  //     document.body.style.width = '100%'
  //   } else {
  //     // enable body scroll
  //     const scrollY = document.body.style.top
  //     document.body.style.position = ''
  //     document.body.style.top = ''
  //     document.body.style.width = ''
  //     document.body.style.margin = ''
  //     window.scrollTo(0, parseInt(scrollY || '0') * -1)
  //   }
  // }

  function onSwipe() {
    selectedId.set(undefined)
  }

  onMount(function () {
    document.addEventListener('swipe-right', onSwipe)
    document.addEventListener('swipe-left', onSwipe)
    return () => {
      document.removeEventListener('swipe-right', onSwipe)
      document.removeEventListener('swipe-left', onSwipe)
    }
  })
</script>

{#if $selectedPlayer}
  {@const { player, alignment, stats } = $selectedPlayer}
  <div
    class="fixed w-full h-full top-0 left-0 z-[100] bg-gray-6/80 dark:bg-gray-4/80 overflow-auto"
    on:click={() => selectedId.set(undefined)}
  >
    <div
      class="px-2 py-2 flex flex-col md:flex-row items-start gap-2 w-full md:w-fit absolute left-1/2 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2"
      class:top-2={!$isNativeMobile}
      class:top-10={$isNativeMobile}
      on:click|stopPropagation
    >
      <div class="flex flex-col gap-2 w-full md:w-[380px] md:-mt-11">
        <div
          class="w-fit p-2 bg-gray-8 dark:bg-gray-3 text-inherit border border-gray-6 dark:border-gray-3 rounded-full hover:cursor-pointer"
          on:click={() => selectedId.set(undefined)}
        >
          <Icon name="x" class="w-4 h-4" />
        </div>
        <Panel>
          <div
            class="flex items-center gap-1 border-b border-gray-6 dark:border-gray-4 -mx-3 px-3"
          >
            <Image
              class="h-[75px] max-w-[100px] select-none object-contain object-bottom"
              alt={player?.longName ?? ''}
              src={player?.media.bustOrSilhouette?.imageUrl ?? ''}
              width={180}
              height={180}
              loading="eager"
            />
            <div>
              <EntityLink
                entity={{
                  type: 'player',
                  id: player?.id,
                  display: player?.longName ?? '',
                  domain,
                }}
                class="whitespace-nowrap text-inherit"
              >
                {player?.longName ?? ''}
              </EntityLink>
              <div
                class="flex items-center justify-between gap-2 text-sm text-gray-5"
              >
                <EntityLink
                  entity={undefined}
                  class="whitespace-nowrap text-inherit"
                >
                  {dayjs(gameTimestamp).format('MMM D')}
                </EntityLink>
                <EntityLink
                  entity={undefined}
                  class="flex gap-1 items-center text-inherit"
                >
                  <p class="whitespace-nowrap">
                    {alignment === 'away' ? 'at' : 'vs'}
                  </p>
                  <div class="flex gap-1 items-center">
                    <Image
                      class="h-4 max-w-4 select-none object-contain object-center"
                      alt={opponentTeam?.name ?? ''}
                      src={opponentTeam?.logoImageUrl ?? ''}
                      width={16}
                      height={16}
                      loading="eager"
                    />

                    <p class="whitespace-nowrap">
                      {opponentTeam?.abbreviation ?? ''}
                    </p>
                  </div>
                </EntityLink>
              </div>
            </div>
          </div>
          <div class="flex flex-wrap gap-3 py-3 leading-snug items-start">
            {#each statSections as section (section)}
              <div class="flex flex-col items-center">
                <div class="whitespace-nowrap mb-[2px]">
                  {#if section.display}
                    {section.display(stats)}
                  {:else}
                    {stats?.[section.statKey]?.display ?? '-'}
                  {/if}
                </div>
                <div
                  class="font-light text-sm opacity-80 whitespace-pre text-center leading-[1.1]"
                >
                  {section.key}
                </div>
              </div>
            {/each}
          </div>
        </Panel>
      </div>
    </div>
  </div>
{/if}
